import React, { useState } from "react";
import { useSelector } from "react-redux";
import ContinueButton from "../../Registration/ContinueButton";
import { confirmSupplierRegistration } from "../../store/supplierFormDataSlice";
import { useDispatch } from "react-redux";

const ReviewPage = ({ onNext, onEditStep }) => {
  const {
    basicInfo,
    businessLocation,
    registrationAndCompliance,
    servicesAndCapabilities,
    diversityData,
    testimonialsAndReferences,
    membershipsCertifications,
  } = useSelector((state) => state.formData);
  const dispatch = useDispatch();
  const { personalInfo } = useSelector((state) => state.personalInfoFormData);
  const [collapsedSections, setCollapsedSections] = useState({});
    const userId = useSelector((state) => state.auth.id);

  const handleCollapse = (sectionKey) => {
    setCollapsedSections((prev) => ({
      ...prev,
      [sectionKey]: !prev[sectionKey],
    }));
  };

    const handleConfirm = async () => {

      try {
       await dispatch(confirmSupplierRegistration(userId)).unwrap();
      } catch (err) {
        console.error("Failed to confirm registration:", err);
      }
      onNext();
    };

  const formatLocation = () => {
    const { city, stateProvince, country } = personalInfo;
    return [city, stateProvince, country].filter(Boolean).join(", ");
  };


  return (
    <div className="w-full flex flex-col min-h-screen justify-start items-center py-10 px-6">
      <header className="mb-14">
        <h3 className="text-xl font-medium text-[#7E7E7E] text-center">
          Step 9
        </h3>
        <h1 className="text-2xl font-semibold text-[#1D2639] text-center">
          Review
        </h1>
        <p className="text-base font-medium text-[#7E7E7E] text-center mt-2">
          Take a moment to review the details you’ve entered to ensure
          everything is accurate and complete. Confirming your information helps
          us provide the most relevant supplier matches and a smoother
          onboarding experience.
        </p>
      </header>

      {/* STEP 1: PERSONAL DETAILS */}
      <Section
        title="Step 1"
        subtitle="Personal Details"
        collapsed={collapsedSections.step1}
        onCollapse={() => handleCollapse("step1")}
        onEditStep={() => onEditStep("personal-details")}
      >
        <div className="flex items-start text-left mb-4">
          <div className="w-20 h-20 rounded-full bg-gray-200 flex items-start justify-start text-gray-500 overflow-hidden">
            {personalInfo?.profilePicture ? (
              <img
                src={personalInfo?.profilePicture}
                alt="Profile"
                className="w-full h-full object-cover rounded-full"
              />
            ) : (
              <span>No Image</span>
            )}
          </div>
        </div>
        <Field label="First Name" value={personalInfo?.firstName} />
        <Field label="Last Name" value={personalInfo?.lastName} />
        <Field label="Location" value={formatLocation()} />
        <Field label="Personal Details" value={personalInfo?.personalDetails} />
        <Field label="Phone" value={personalInfo?.phone} />
      </Section>

      {/* STEP 2: Basic Supplier Information */}
      <Section
        title="Step 2"
        subtitle="Basic Supplier Information"
        collapsed={collapsedSections.step2}
        onCollapse={() => handleCollapse("step2")}
        onEditStep={() => onEditStep("basic-information")}
      >
        <Field label="Company Name" value={basicInfo?.name} />
        <Field label="Business Legal Name" value={basicInfo?.legalName} />
        <Field label="DBA Name" value={basicInfo?.dba} />
        <Field label="Business Description" value={basicInfo?.description} />
        <Field label="Business Phone" value={basicInfo?.businessPhone} />
        <Field label="Website URL" value={basicInfo?.website} />
        <Field label="Year Registered" value={basicInfo?.foundedYear} />
      </Section>

      {/* STEP 3: Business Location */}
      <Section
        title="Step 3"
        subtitle="Business Location"
        collapsed={collapsedSections.step3}
        onCollapse={() => handleCollapse("step3")}
        onEditStep={() => onEditStep("business-location")}
      >
        <Field label="Country" value={businessLocation?.country} />
        <Field label="Address" value={businessLocation?.address_line_01} />
        <Field label="City" value={businessLocation?.city} />
        <Field label="State/Province" value={businessLocation?.state} />
        <Field label="Zip Code" value={businessLocation?.postal_code} />
      </Section>

      {/* STEP 4: Registration and compliance */}
      <Section
        title="Step 4"
        subtitle="Registration and compliance"
        collapsed={collapsedSections.step4}
        onCollapse={() => handleCollapse("step4")}
        onEditStep={() => onEditStep("registration-compliance")}
      >
        <Field
          label="Tax ID"
          value={registrationAndCompliance?.complianceInfo?.ein?.code}
        />
        <Field
          label="DUNS Number"
          value={registrationAndCompliance?.complianceInfo?.duns?.code}
        />
        <Field
          label="Cage Code"
          value={registrationAndCompliance?.complianceInfo?.cage?.code}
        />
        <Field
          label="Business Entity Type"
          value={registrationAndCompliance?.complianceInfo?.entityType}
        />
        <Field
          label="Verification Document"
          value={registrationAndCompliance?.file?.name}
        />
      </Section>

      {/* STEP 5: Services and capabilities */}
      <Section
        title="Step 5"
        subtitle="Services and capabilities"
        collapsed={collapsedSections.step5}
        onCollapse={() => handleCollapse("step5")}
        onEditStep={() => onEditStep("services-capabilities")}
      >
        <Field label="Industry" value={servicesAndCapabilities?.industry} />
        <Field
          label="Primary NAICS"
          value={servicesAndCapabilities?.primaryNAICS?.code}
        />
        <Field
          label="Additional NAICS"
          value={servicesAndCapabilities?.additionalNAICS
            ?.map((item) => item.code)
            .join(", ")}
        />
      </Section>

      {/* STEP 6: Diversity data */}
      <Section
        title="Step 6"
        subtitle="Diversity Data & Verification"
        collapsed={collapsedSections.step6}
        onCollapse={() => handleCollapse("step6")}
        onEditStep={() => onEditStep("diversity-verification")}
      >
        {Array.isArray(diversityData?.diversityCerts) &&
          diversityData?.diversityCerts.length > 0 && (
            <div className="mt-4">
              {diversityData?.diversityCerts.map((item, idx) => (
                <div key={idx} className="mb-4">
                  <Field label="Diversity Status" value={item?.certName} />
                  <Field label="Certifying Agency" value={item?.agency} />
                  <Field label="Certification ID" value={item?.certId} />
                  <Field
                    label="Diversity Certification Document"
                    value={item?.file?.name}
                  />
                </div>
              ))}
            </div>
          )}
      </Section>

      {/* STEP 7: Testimonial and references */}
      <Section
        title="Step 7"
        subtitle="Testimonial and references"
        collapsed={collapsedSections.step7}
        onCollapse={() => handleCollapse("step7")}
        onEditStep={() => onEditStep("testimonials-references")}
      >
        {Array.isArray(testimonialsAndReferences) &&
          testimonialsAndReferences.length > 0 && (
            <div className="mt-4">
              {testimonialsAndReferences.map((item, idx) => (
                <div key={idx} className="mb-4">
                  <Field
                    label="Type"
                    value={item?.reference ? "References" : "Testimonial"}
                  />
                  <Field label="Name" value={item?.endorsementInf?.name} />
                  <Field
                    label={item?.reference ? "Contact Info" : "Feedback"}
                    value={
                      item.reference
                        ? item?.endorsementInfo?.contact
                        : item?.endorsementInfo?.feedback
                    }
                  />
                  <Field
                    label="Document"
                    value={item?.endorsementInfo?.file?.name}
                  />
                </div>
              ))}
            </div>
          )}
      </Section>

      {/* STEP 8: Membership Associations & Certifications */}
      <Section
        title="Step 8"
        subtitle="Membership Associations & Certifications"
        collapsed={collapsedSections.step8}
        onCollapse={() => handleCollapse("step8")}
        onEditStep={() => onEditStep("membership-certifications")}
      >
        {/* Memberships */}
        {membershipsCertifications?.memberships?.length > 0 && (
          <div className="mb-6 text-left">
            <h3 className="text-[#7E7E7E] text-sm mb-2">
              Membership Associations
            </h3>
            <ul className="list-disc list-inside space-y-1">
              {membershipsCertifications.memberships.map((item, idx) => (
                <li key={idx} className="pl-4">
                  {item.name} {item.acronym ? `(${item.acronym})` : ""}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Certifications */}
        {membershipsCertifications?.certifications?.length > 0 && (
          <div>
            <h3 className="text-[#7E7E7E] text-sm mb-2">
              Supplier Certifications
            </h3>
            <ul className="list-square space-y-1">
              {membershipsCertifications?.certifications?.map((item, idx) => (
                <li className="pl-4" key={idx}>
                  {item.name}
                </li>
              ))}
            </ul>
          </div>
        )}
      </Section>

      <div className="mt-8 text-start w-full sm:w-[572px]">
        <ContinueButton onClick={handleConfirm} label="Save and Continue" />
      </div>
    </div>
  );
};

const Section = ({
  title,
  subtitle,
  children,
  collapsed,
  onCollapse,
  onEditStep,
}) => {
  return (
    <div className="mt-10 border-b pb-6 w-full flex justify-between items-start">
      <div className="flex flex-col flex-grow">
        <div className="flex items-start justify-between text-left">
          <div>
            <h3 className="text-xl text-gray-500 font-semibold mb-1 text-left whitespace-nowrap">
              {title}
            </h3>
            <h2 className="text-2xl font-bold text-gray-800 mb-4">
              {subtitle}
            </h2>
          </div>

          <div className="flex flex-row justify-center items-center gap-2 ml-4">
            <button className="text-base text-[#02AD28] bg-transparent p-0 md:bg-green-100 md:px-4 md:py-2 rounded flex flex-row">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="25"
                viewBox="0 0 24 25"
                fill="none"
                className="md:mr-2 mr-0"
              >
                <path
                  d="M9.54972 15.65L18.0247 7.175C18.2247 6.975 18.4581 6.875 18.7247 6.875C18.9914 6.875 19.2247 6.975 19.4247 7.175C19.6247 7.375 19.7247 7.6125 19.7247 7.8875C19.7247 8.1625 19.6247 8.4 19.4247 8.6L10.2497 17.8C10.0497 18 9.81639 18.1 9.54972 18.1C9.28305 18.1 9.04972 18 8.84972 17.8L4.54972 13.5C4.34972 13.3 4.25389 13.0625 4.26222 12.7875C4.27055 12.5125 4.37472 12.275 4.57472 12.075C4.77472 11.875 5.01222 11.775 5.28722 11.775C5.56222 11.775 5.79972 11.875 5.99972 12.075L9.54972 15.65Z"
                  fill="#02AD28"
                />
              </svg>
              <span className="hidden md:inline ml-2 text-[#02AD28] text-base">
                Completed
              </span>
            </button>
            <button
              onClick={onEditStep}
              className="text-base text-[#1D2739] md:bg-[#F0F0F0] md:px-4 md:py-2 p-0 rounded flex flex-row"
            >
              <span className="hidden md:inline md:ml-2 ml-1 text-[#1D2739] text-base">
                Edit
              </span>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="25"
                viewBox="0 0 24 25"
                fill="none"
                className="md:ml-2 ml-0"
              >
                <path
                  d="M7 7.5H6C5.46957 7.5 4.96086 7.71071 4.58579 8.08579C4.21071 8.46086 4 8.96957 4 9.5V18.5C4 19.0304 4.21071 19.5391 4.58579 19.9142C4.96086 20.2893 5.46957 20.5 6 20.5H15C15.5304 20.5 16.0391 20.2893 16.4142 19.9142C16.7893 19.5391 17 19.0304 17 18.5V17.5"
                  stroke="black"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
                <path
                  d="M16 5.50011L19 8.50011M20.385 7.08511C20.7788 6.69126 21.0001 6.15709 21.0001 5.60011C21.0001 5.04312 20.7788 4.50895 20.385 4.11511C19.9912 3.72126 19.457 3.5 18.9 3.5C18.343 3.5 17.8088 3.72126 17.415 4.11511L9 12.5001V15.5001H12L20.385 7.08511Z"
                  stroke="black"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </button>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="25"
              viewBox="0 0 24 25"
              fill="none"
              className="md:ml-2 ml-0 cursor-pointer"
              onClick={onCollapse}
            >
              <path
                d="M4.5 16.25L12 8.75L19.5 16.25"
                stroke="#1D2739"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </div>
        </div>

        {!collapsed && <div className="mt-4">{children}</div>}
      </div>
    </div>
  );
};

const Field = ({ label, value }) => (
  <div className="mb-6 flex justify-start items-start flex-col w-full">
    <div className="text-[14px] text-[#7E7E7E]">{label}</div>
    <div className="text-[16px] text-black">{value || "—"}</div>
  </div>
);

export default ReviewPage;
